import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GetNotifications, GetNotificationsUnread, MarkAllSeen, MarkAsRead } from "../../services/Common.ts";
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaInfoCircle,
  FaTimes,
  FaTrash,
  FaEye,
  FaChevronRight,
  FaBell,
  FaArrowLeft
} from "react-icons/fa";
import Pagination from "../../components/Pagination.tsx";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { themes } from "../../theme.ts";

interface Notification {
  id: number;
  title: string;
  description: string;
  type: 'info' | 'warning' | 'success' | 'error';
  read: boolean;
  created_at: string;
  action_url?: string;
}



export const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const { themeName } = useTheme();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const rawData = localStorage.getItem('user');
  const cred = rawData ? JSON.parse(rawData) : null;
  const token = cred?.access;

  const fetchAllNotifications = async () => {
    const id = cred?.user?.id;
    try {
      const response = await GetNotifications(token, id);
      // console.log('resp', response)
      setNotifications((response as any)?.data || []);
    //   updateUnreadCount(response?.data || []);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  };

  const fetchUnreadNotifications = async () =>{
    const id = cred?.user?.id;
    try {
      const response = await GetNotificationsUnread(token);
    //   setNotifications(response?.data || []);
    //   updateUnreadCount(response?.data || []);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  }

  const MarkAllRead = async () =>{
    const id = cred?.user?.id;
    try {
      const response = await MarkAllSeen(token);
      // Update local state to mark all as read immediately
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      // Optionally refetch to ensure consistency
      // await fetchAllNotifications();
    } catch (err) {
      console.error("Error marking all notifications as seen:", err);
    }
  }

  const updateUnreadCount = (notifs: Notification[]) => {
    // setUnreadCount(notifs.filter(n => !n.read).length);
  };

  const markAsRead = async (id: number) => {
    try {
      await MarkAsRead(token, id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => prev - 1);
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const deleteNotification = (id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  useEffect(() => {
    fetchAllNotifications();
  }, []);

  const getNotificationIcon = (type: Notification['type']) => {
    const iconClass = "text-lg mr-3";
    switch (type) {
      case 'success': return <FaCheckCircle className={`${iconClass} text-green-500`} />;
      case 'warning': return <FaExclamationCircle className={`${iconClass} text-yellow-500`} />;
      case 'error': return <FaExclamationCircle className={`${iconClass} text-red-500`} />;
      default: return <FaInfoCircle className={`${iconClass} text-blue-500`} />;
    }
  };

  return (
    <div className=" mx-auto" style={{ backgroundColor: themes[themeName].colors.background }}>
      {/* Back Button */}
      <div className="mb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-sm font-medium hover:underline"
          style={{ color: themes[themeName].colors.primary }}
        >
          <FaArrowLeft className="mr-2" />
          Back
        </button>
      </div>

      {/* Header */}
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl font-bold flex items-center" style={{ color: themes[themeName].colors.text }}>
          <FaBell className="mr-3 text-blue-500" />
          Notifications
          {unreadCount > 0 && (
            <span className="ml-3 bg-blue-500 text-white text-sm rounded-full px-2 py-1">
              {unreadCount} unread
            </span>
          )}
        </h1>
        <button
          onClick={MarkAllRead}
          className="flex items-center"
          style={{ color: themes[themeName].colors.primary }}
        >
          <FaCheckCircle className="mr-2" />
          Mark all as read
        </button>
      </div>

      {/* Notifications List */}
      <div className="rounded-lg shadow overflow-hidden" style={{ backgroundColor: themes[themeName].colors.card, borderColor: themes[themeName].colors.border }}>
        {notifications?.length === 0 ? (
          <div className="p-8 text-center" style={{ color: themes[themeName].colors.textSecondary }}>
            <FaBell className="mx-auto text-4xl mb-4" style={{ color: themes[themeName].colors.textMuted }} />
            <p className="text-xl">No notifications yet</p>
            <p className="mt-2">We'll notify you when something arrives</p>
          </div>
        ) : (
          <ul className="divide-y" style={{ borderColor: themes[themeName].colors.border }}>
            {notifications?.map((notification) => (
              <li
                key={notification?.id}
                className={`${!notification?.read ? '' : ''}`}
              >
                <div
                  className="flex p-4"
                  style={{ backgroundColor: !notification?.read ? themes[themeName].colors.surface : themes[themeName].colors.card }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themes[themeName].colors.surface}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = !notification?.read ? themes[themeName].colors.surface : themes[themeName].colors.card}
                >
                  <div className="flex-shrink-0">
                    {getNotificationIcon(notification?.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between">
                      <p className={`text-md font-medium ${!notification?.read ? '' : ''}`} style={{ color: !notification?.read ? themes[themeName].colors.text : themes[themeName].colors.textSecondary }}>
                        {notification?.title}
                      </p>
                      <div className="flex space-x-3 ml-2">
                        {!notification.read && (
                          <button
                            onClick={() => markAsRead(notification?.id)}
                            className="text-gray-500 hover:text-gray-700"
                            title="Mark as read"
                            style={{ color: themes[themeName].colors.textMuted }}
                          >
                            <FaEye />
                          </button>
                        )}
                        <button
                          onClick={() => deleteNotification(notification?.id)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div> 
                    </div>
                    <p className="text-sm mt-1" style={{ color: themes[themeName].colors.textMuted }}>
                      {notification.description}
                    </p>
                    <div className="mt-2 flex justify-between items-center">
                      <span className="text-xs" style={{ color: themes[themeName].colors.textMuted }}>
                        {formatTime(notification.created_at)}
                      </span>
                      {/* {notification.action_url && (
                        <a
                          href={notification.action_url}
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          View details <FaChevronRight className="ml-1" />
                        </a>
                      )} */}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Pagination onPageChange={() => {}} />
    </div>
  );
};